import React, { useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { nanoid } from 'nanoid';

import CountingButton from 'components/atoms/CountingButton';
import Button from 'components/atoms/Button';
import { useToast } from 'models/toast';
import { debounce } from 'util/debounce';
import styles from './index.module.css';

interface AddToCartBtnsProperty {
	className?: string;
	disabled?: boolean;
	direction?: 'row' | 'column';
	productQuantityinCart: number;
	type?: 'add-on';
	max?: number;
	handleAddToCartSubmit: (arg0: number, arg1: number) => void;
}

const AddToCartBtns: React.FC<AddToCartBtnsProperty> = ({
	className,
	disabled,
	direction = 'row',
	productQuantityinCart,
	type = '',
	max = Number.MAX_SAFE_INTEGER,
	handleAddToCartSubmit,
}) => {
	const [, { toast }] = useToast();
	const [count, setCount] = useState(productQuantityinCart || 1);
	const diffFromPrevious = useRef(1); // 按下新增按鈕時，比較前次的數量變化
	const minusDisabled = useMemo(
		() => count === 1 && productQuantityinCart === 0,
		[count, productQuantityinCart],
	);

	useEffect(() => {
		if (productQuantityinCart) {
			setCount(productQuantityinCart);
		} else {
			setCount(1);
		}
	}, [productQuantityinCart]);

	const plus = () => {
		if (count >= max) return;
		if (count >= max - 1) {
			const id = nanoid();
			toast({
				id,
				message: '已達購買上限',
				type: 'warn',
				color: 'red',
			});
		}
		setCount(c => c + 1);
		diffFromPrevious.current += 1;
	};

	const minus = () => {
		setCount(c => (c <= 0 ? 0 : c - 1));
		diffFromPrevious.current -= 1;
	};

	const reset = () => {
		setCount(1);
	};

	const buttonTitle = useMemo(() => {
		if (disabled || max === 0) {
			return '售完補貨中';
		}
		if (count === productQuantityinCart) {
			return `購物車 (${count})`;
		}
		if (productQuantityinCart !== 0 && count !== productQuantityinCart) {
			return '更新數量';
		}
		return '加入購物車';
	}, [count, productQuantityinCart, max, disabled]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		const change = () => {
			const regex = /[^0-9]/g; // 匹配除了数字以外的所有字符
			const result = Number(value.replace(regex, ''));
			if (result >= max) {
				const id = nanoid();
				toast({
					id,
					message: '已達購買上限',
					type: 'warn',
					color: 'red',
				});
				setCount(max);
				diffFromPrevious.current = max - productQuantityinCart;
			} else {
				setCount(result);
				diffFromPrevious.current = result - productQuantityinCart;
			}
		};
		debounce(change)();
	};

	const handleClick = () => {
		const id = nanoid();
		if (productQuantityinCart === 0 && count > 0) {
			toast({
				id,
				message: '已加入購物車',
				type: 'success',
				color: 'green',
			});
		} else if (count === 0 && count !== productQuantityinCart) {
			toast({
				id,
				message: '已移除商品',
				type: 'success',
				color: 'purple',
			});
			reset();
		} else if (productQuantityinCart > 0 && count !== productQuantityinCart) {
			toast({
				id,
				message: '數量已更新',
				type: 'success',
				color: 'green',
			});
		} else if (count > max) {
			toast({
				id,
				message: '已達購買上限',
				type: 'success',
				color: 'red',
			});
		}
		handleAddToCartSubmit(count, diffFromPrevious.current);
		diffFromPrevious.current = 0;
		if (count === 0) {
			diffFromPrevious.current = 1;
		}
	};

	useEffect(() => {
		if (max < count) {
			setCount(max);
		}
	}, [max, count]);

	return (
		<div className={classnames(styles.addToCartBtns, className, styles[direction], styles[type])}>
			<CountingButton
				count={count}
				plus={plus}
				minus={minus}
				disabled={disabled || max === 0}
				className={styles.countingButton}
				max={max}
				handleChange={handleChange}
				minusDisabled={minusDisabled}
			/>
			<Button
				size={type === 'add-on' ? 'small' : 'medium'}
				pattern="primaryOutline"
				name={buttonTitle}
				handleClick={handleClick}
				className={classnames(
					styles.addToCart,
					count === productQuantityinCart && max !== 0 && styles.deactive,
				)}
				disabled={disabled || max === 0}
			/>
		</div>
	);
};

export default AddToCartBtns;
